/**
 * ClickTarget router — FE-NAV click model (counts → lists, items → chat, warnings → chat, criticals → Exo).
 */

export type ClickRoute =
  | { kind: 'list_items'; stageId: string }
  | { kind: 'chat'; itemId?: string; context?: string }
  | { kind: 'exo'; itemId: string }
  | { kind: 'none' };

export type BlockerClass = 'none' | 'warning' | 'critical';

export interface ClickableTarget {
  /** Stage / count click */
  stageId?: string;
  wipCount?: number;
  /** Item click */
  itemId?: string;
  /** Encoded blocker class from model (FE-NAV-3/4) */
  blockerClass?: BlockerClass;
  /** True when a closable transition exists (critical → Exo) */
  closable?: boolean;
  /** Explicit override from affordance */
  forceRoute?: ClickRoute['kind'];
}

export function normalizeBlockerClass(raw: string | undefined): BlockerClass {
  if (!raw) return 'none';
  const v = raw.trim().toLowerCase();
  if (v === 'critical' || v === 'held' || v === 'p0') return 'critical';
  if (
    v === 'warning' ||
    v === 'missing-info' ||
    v === 'missing_info' ||
    v === 'grouping' ||
    v === 'store' ||
    v === 'supplier'
  ) {
    return 'warning';
  }
  if (v === 'none' || v === '') return 'none';
  return 'none';
}

/**
 * Resolve a UI click to a route (FE-NAV-1..5).
 * - Count / stage → list_items
 * - Item (no special blocker) → chat
 * - Warning (non-closable) → chat
 * - Critical closable → exo
 */
export function resolveClickTarget(target: ClickableTarget): ClickRoute {
  if (target.forceRoute === 'list_items' && target.stageId) {
    return { kind: 'list_items', stageId: target.stageId };
  }
  if (target.forceRoute === 'exo' && target.itemId) {
    return { kind: 'exo', itemId: target.itemId };
  }
  if (target.forceRoute === 'chat') {
    return { kind: 'chat', itemId: target.itemId, context: target.stageId };
  }

  // Stage / count (no item)
  if (target.stageId && !target.itemId) {
    return { kind: 'list_items', stageId: target.stageId };
  }

  const blocker = target.blockerClass ?? 'none';
  if (blocker === 'critical' && target.closable !== false && target.itemId) {
    return { kind: 'exo', itemId: target.itemId };
  }
  if (blocker === 'warning') {
    return { kind: 'chat', itemId: target.itemId, context: 'warning' };
  }
  if (target.itemId) {
    return { kind: 'chat', itemId: target.itemId };
  }
  return { kind: 'none' };
}
